import { useMemo, useState } from 'react';

import { Box, Button, Center, Flex, Grid, Image, Text } from '@chakra-ui/react';
import { Badge, RecognitionCardItem, RecognitionValue } from '@aslanpay/portal-common-lib';

import CardPickerModal, { MAX_CARDS_PER_VALUE } from './CardPickerModal';

/** The cards chosen for each value, by value id. */
export type ValueCardSelection = Record<string, string[]>;

export interface CardOption {
  id: string;
  contentUrl: string;
}

export interface ValueCardsEditorProps {
  /** The pride this tab edits, or absent for the campaign's default. */
  groupId?: string;
  values: RecognitionValue[];
  /** The company's card library and its campaign-only uploads, offered for every value. */
  libraryCards: RecognitionCardItem[];
  /** The saved choice, or the default's for a pride using it. A value with none starts on its first card. */
  initialSelection: ValueCardSelection;
  /** What the tab is for, shown above the cards. */
  description: string;
  /** A pride with no cards of its own announces with the default's. */
  usingDefault?: boolean;
  isResetting?: boolean;
  /** Puts a pride with its own cards back on the default. */
  onReset?: () => void;
  /** The cards already saved, so a card no longer offered can still be seen and removed. */
  savedCards: CardOption[];
  isSaving: boolean;
  /** Any save or reset in flight, for any tab: nothing else can be sent until it settles. */
  isUpdating?: boolean;
  onSave: (selection: ValueCardSelection) => void;
  /** Uploads a campaign-only card, resolving to its id, or null when the upload failed. */
  onUpload: (file: File) => Promise<string | null>;
}

/**
 * The cards one value may be shown with, each once: its own first (so its first card leads, the one a winner gets when
 * nothing is saved), then the company's card library and campaign-only uploads.
 */
export function cardOptionsFor(
  value: RecognitionValue,
  libraryCards: CardOption[],
  savedCards: CardOption[],
  selected: string[]
): CardOption[] {
  const options = new Map<string, CardOption>();

  value.cards.forEach((card) => options.set(card.id, card));
  libraryCards.forEach((card) => options.set(card.id, card));
  savedCards
    .filter((card) => selected.includes(card.id))
    .forEach((card) => options.set(card.id, card));

  return [...options.values()];
}

const sameSelection = (a: ValueCardSelection, b: ValueCardSelection, valueIds: string[]) =>
  valueIds.every((valueId) => {
    const left = [...(a[valueId] ?? [])].sort();
    const right = [...(b[valueId] ?? [])].sort();

    return left.length === right.length && left.every((cardId, index) => cardId === right[index]);
  });

function ValueCardsEditor({
  groupId = undefined,
  values,
  libraryCards,
  initialSelection,
  description,
  savedCards,
  usingDefault = false,
  isResetting = false,
  isSaving,
  isUpdating = false,
  onSave,
  onReset = undefined,
  onUpload,
}: ValueCardsEditorProps) {
  const scope = groupId ?? 'default';
  const [pickingFor, setPickingFor] = useState<RecognitionValue>();

  // A value with nothing saved starts on its first card: the same card a winner gets when nothing is saved, so
  // saving as-is changes nothing for them.
  const [startingSelection] = useState<ValueCardSelection>(() =>
    values.reduce<ValueCardSelection>((started, value) => {
      const [firstCard] = cardOptionsFor(value, libraryCards, savedCards, []);

      if (!started[value.id]?.length && firstCard) {
        return { ...started, [value.id]: [firstCard.id] };
      }

      return started;
    }, initialSelection)
  );
  const [selection, setSelection] = useState<ValueCardSelection>(startingSelection);

  const valueIds = useMemo(() => values.map((value) => value.id), [values]);
  // A pride using the default has nothing of its own to save until a card is changed. Anywhere else, a first card
  // chosen on the admin's behalf is not saved yet, so Save offers to save it.
  const isChanged = !sameSelection(
    selection,
    usingDefault ? startingSelection : initialSelection,
    valueIds
  );

  const cardsById = useMemo(
    () =>
      new Map(
        [...values.flatMap((value) => value.cards), ...libraryCards, ...savedCards].map((card) => [
          card.id,
          card,
        ])
      ),
    [values, libraryCards, savedCards]
  );

  const addCards = (valueId: string, cardIds: string[]) =>
    setSelection((previous) => {
      const current = previous[valueId] ?? [];

      return {
        ...previous,
        [valueId]: [...current, ...cardIds.filter((cardId) => !current.includes(cardId))],
      };
    });

  // Every value keeps at least one card, so its last card cannot be removed.
  const removeCard = (valueId: string, cardId: string) =>
    setSelection((previous) => {
      const current = previous[valueId] ?? [];

      if (current.length === 1) {
        return previous;
      }

      return { ...previous, [valueId]: current.filter((id) => id !== cardId) };
    });

  const valuesMissingACard = values.filter((value) => !selection[value.id]?.length);

  return (
    <Flex flexDir="column" gap="24px" data-testid={`value-cards-editor-${scope}`}>
      <Flex
        align={{ base: 'flex-start', md: 'center' }}
        justify="space-between"
        gap="16px"
        direction={{ base: 'column', md: 'row' }}
      >
        <Flex align="center" gap="12px" wrap="wrap">
          {usingDefault && <Badge value="Using default" status="created" />}
          <Text textStyle="smallRegular" color="brand.darkGray" maxW="520px">
            {description}
          </Text>
        </Flex>
        <Flex gap="12px" flexShrink={0}>
          {groupId && !usingDefault && onReset && (
            <Button
              variant="outline"
              isLoading={isResetting}
              isDisabled={isUpdating}
              onClick={onReset}
              data-testid={`reset-value-cards-${scope}`}
            >
              Reset to default
            </Button>
          )}
          <Button
            colorScheme="purple"
            isLoading={isSaving}
            isDisabled={isUpdating || !isChanged || valuesMissingACard.length > 0}
            onClick={() => onSave(selection)}
            data-testid={`save-value-cards-${scope}`}
          >
            Save
          </Button>
        </Flex>
      </Flex>
      {isChanged && valuesMissingACard.length > 0 && (
        <Text
          textStyle="smallRegular"
          color="text.error"
          data-testid={`value-cards-missing-${scope}`}
        >
          Choose a card for {valuesMissingACard.map((value) => value.name).join(', ')} before
          saving.
        </Text>
      )}

      <Flex flexDir="column" gap="16px">
        {values.map((value) => {
          const selected = selection[value.id] ?? [];
          const canRemove = selected.length > 1;

          return (
            <Flex
              key={value.id}
              flexDir="column"
              gap="16px"
              p="20px"
              borderWidth="1px"
              borderColor="brand.lightGray1"
              borderRadius="12px"
              data-testid={`value-cards-${scope}-${value.id}`}
            >
              <Flex align="center" justify="space-between" gap="12px">
                <Text textStyle="smallSemiBold" color="brand.black">
                  {value.emoji} {value.name}
                </Text>
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  {selected.length === 1 ? '1 card' : `${selected.length} cards`}
                </Text>
              </Flex>
              <Grid templateColumns="repeat(auto-fill, minmax(140px, 1fr))" gap="12px">
                {selected.map((cardId, index) => {
                  const card = cardsById.get(cardId);

                  return (
                    <Box
                      key={cardId}
                      position="relative"
                      h="100px"
                      borderWidth="2px"
                      borderRadius="10px"
                      borderColor="brand.purple"
                      overflow="hidden"
                      bg="brand.lightGray1"
                      data-testid={`value-card-${scope}-${value.id}-${cardId}`}
                    >
                      {card && (
                        <Image src={card.contentUrl} alt="" h="full" w="full" objectFit="cover" />
                      )}
                      {canRemove && (
                        <Center
                          as="button"
                          type="button"
                          position="absolute"
                          top="8px"
                          right="8px"
                          boxSize="24px"
                          borderRadius="full"
                          bg="white"
                          color="brand.black"
                          fontSize="14px"
                          lineHeight="1"
                          boxShadow="sm"
                          _hover={{ bg: 'brand.purple', color: 'white' }}
                          onClick={() => removeCard(value.id, cardId)}
                          aria-label={`Remove card ${index + 1} from ${value.name}`}
                          data-testid={`remove-value-card-${scope}-${value.id}-${cardId}`}
                        >
                          ×
                        </Center>
                      )}
                    </Box>
                  );
                })}
                <Center
                  as="button"
                  type="button"
                  h="100px"
                  borderWidth="2px"
                  borderStyle="dashed"
                  borderRadius="10px"
                  borderColor="brand.lightGray1"
                  color="brand.darkGray"
                  fontSize="28px"
                  lineHeight="1"
                  transition="border-color 0.15s ease, color 0.15s ease"
                  _hover={{ borderColor: 'brand.purple', color: 'brand.purple' }}
                  onClick={() => setPickingFor(value)}
                  aria-label={`Add cards for ${value.name}`}
                  data-testid={`add-value-cards-${scope}-${value.id}`}
                >
                  +
                </Center>
              </Grid>
            </Flex>
          );
        })}
      </Flex>

      {pickingFor && (
        <CardPickerModal
          isOpen
          valueName={pickingFor.name}
          options={cardOptionsFor(pickingFor, libraryCards, savedCards, []).filter(
            (card) => !(selection[pickingFor.id] ?? []).includes(card.id)
          )}
          onClose={() => setPickingFor(undefined)}
          onAdd={(cardIds) => addCards(pickingFor.id, cardIds)}
          maxToAdd={Math.max(0, MAX_CARDS_PER_VALUE - (selection[pickingFor.id] ?? []).length)}
          onUpload={onUpload}
        />
      )}
    </Flex>
  );
}

export default ValueCardsEditor;
